import * as topojson from "topojson-client"
import { Mercator } from "@visx/geo"
import { scaleSqrt } from "@visx/scale"
import { ParentSizeModern } from "@visx/responsive"

import topology from "./world-topo.json"

const background = "#fff"
const fill = "#e7e6e6"
interface FeatureShape {
  type: "Feature"
  id: string
  geometry: { coordinates: [number, number][][]; type: "Polygon" }
  properties: { name: string }
}

// @ts-ignore
const world = topojson.feature(topology, topology.objects.units) as {
  type: "FeatureCollection"
  features: FeatureShape[]
}

type WorldBubblesProps<T extends Record<string, any>> = {
  width: number
  height: number

  data: T[]
  getCountry: (x: T) => string
  getValue: (x: T) => number
  color?: string
  opacity?: number
}

function WorldBubbles<T extends Record<string, any>>(
  props: WorldBubblesProps<T>,
) {
  const {
    width,
    height,
    data,
    getCountry,
    getValue,
    //
    color = "#33c1b6",
    opacity = 0.4,
    //
  } = props

  const centerX = width / 2
  const centerY = height / 2
  const scale = (width / 630) * 100

  const countries = data.map(getCountry)
  const max = Math.max(...data.map(getValue))
  const radius = scaleSqrt({
    domain: [0, max],
    range: [4, 40],
  })
  const record = Object.fromEntries(
    data.map((x) => [getCountry(x), getValue(x)]),
  )

  return (
    <svg width={width} height={height}>
      <rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={background}
        rx={14}
      />
      <Mercator<FeatureShape>
        data={world.features}
        scale={scale}
        translate={[centerX, centerY + 50]}
      >
        {(mercator) => (
          <g>
            {mercator.features.map(({ path }, idx) => {
              return (
                <g key={`map-feature-${idx}`}>
                  <path
                    d={path || ""}
                    fill={fill}
                    stroke={background}
                    strokeWidth={0.5}
                    // onClick={() => {
                    //   console.log(`${feature.properties.name}, ${feature.id}`)
                    // }}
                  />
                </g>
              )
            })}
            {mercator.features
              .filter((x) => countries.includes(x.feature.properties.name))
              .map(({ feature, centroid }) => {
                const country = feature.properties.name
                const value = record[country] ?? 0
                return { value, position: centroid }
              })
              .sort((a, b) => b.value - a.value)
              .map(({ value, position }, idx) => {
                if (value === 0) return null
                const [x, y] = position
                return (
                  <g key={"c" + idx} transform={`translate(${x} ${y})`}>
                    <circle
                      r={radius(value)}
                      fill={color}
                      fillOpacity={opacity}
                      stroke="#fff"
                      strokeWidth="0.5"
                    />
                  </g>
                )
              })}
          </g>
        )}
      </Mercator>
    </svg>
  )
}

export function ResponsiveWorldBubbles<T extends Record<string, any>>(
  props: Omit<WorldBubblesProps<T>, "width" | "height">,
) {
  return (
    <ParentSizeModern enableDebounceLeadingCall>
      {(s) => {
        return <WorldBubbles width={s.width} height={s.height} {...props} />
      }}
    </ParentSizeModern>
  )
}
